import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReceiptText } from "iconsax-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { CommissionSettlement } from "@/api/schemas/commissionSettlement";
import { CommissionStatus } from "@/api/schemas/commissionStatus";
import type { StatusBadgeColor } from "@/components/Atoms/StatusBadge/StatusBadge.types";
import { StatusBadge } from "@/components/Atoms";
import type { BillingCommissionView } from "@/components/Templates/Billing/Billing.template.types";
import * as styles from "../BillingInvoicesList/BillingInvoicesList.styles";
import type { BillingCommissionsListProps } from "./BillingCommissionsList.types";

export type { BillingCommissionsListProps } from "./BillingCommissionsList.types";

function resolveBadge(commission: BillingCommissionView): { labelKey: string; color: StatusBadgeColor } {
  if (commission.settlement === CommissionSettlement.split) {
    return { labelKey: "commissions.split", color: "info" };
  }
  if (commission.status === CommissionStatus.settled) {
    return { labelKey: "commissions.settled", color: "success" };
  }
  return { labelKey: "commissions.debt", color: "warning" };
}

export const BillingCommissionsList = memo(function BillingCommissionsList({
  commissions,
}: BillingCommissionsListProps) {
  const { t } = useTranslation("billing");
  const theme = useTheme();

  if (commissions.length === 0) return null;

  return (
    <Box sx={styles.card}>
      <Typography component="h3" sx={styles.title}>
        {t("commissions.title")}
      </Typography>

      {commissions.map((commission) => {
        const badge = resolveBadge(commission);
        const label = commission.orderNumber
          ? t("commissions.order", { number: commission.orderNumber })
          : t("commissions.manualDebt");

        return (
          <Box key={commission.id}>
            <Box sx={styles.desktopRow}>
              <Box sx={styles.desktopLeft}>
                <ReceiptText size={16} variant="Bold" color={theme.palette.text.secondary} />
                <Typography component="span">{commission.shopName}</Typography>
                <Typography component="span" sx={styles.secondaryText}>
                  {label} · {commission.date}
                </Typography>
                <Typography component="span" sx={styles.secondaryText}>
                  {t("commissions.orderAmount", { amount: commission.orderAmount })}
                </Typography>
              </Box>
              <Box sx={styles.desktopRight}>
                <Typography sx={styles.desktopAmount}>
                  {commission.amount} {t("stats.currency")}
                </Typography>
                <StatusBadge color={badge.color} size="sm" label={t(badge.labelKey)} />
              </Box>
            </Box>

            <Box sx={styles.mobileRow}>
              <Box sx={styles.mobileTop}>
                <Typography sx={styles.mobileShopName}>{label}</Typography>
                <Typography sx={styles.mobileAmount}>
                  {commission.amount} {t("stats.currency")}
                </Typography>
              </Box>
              <Box sx={styles.mobileTop}>
                <Typography sx={styles.mobileDate}>
                  {commission.shopName} · {commission.date}
                </Typography>
                <StatusBadge color={badge.color} size="sm" label={t(badge.labelKey)} />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
});
